import { Axios } from './axios'
import { AXIOS_METHODS } from '../utils/constants/enum'
import { EDVR_ENDPOINTS } from '../utils/constants/endpoints'
import {
    LoginFormTypes,
    LogoutTypes,
    ResetPasswordFormTypes,
    SetPasswordFormTypes,
    UpdatePasswordTypes,
    UserDetailsTypes,
    UserEndeavorPreferences,
    UserRegistrationTypes,
} from '../utils/types/auth'
import { MESSAGES } from '../utils/constants'
import {
    BucketResponse,
    FavouritesResponse,
    PlaylistResponse,
    RedeemPin,
    SectionResponse,
    EventsResponse,
    UserLoginResponse,
    VideoResponse,
    VideosByTagsResponse,
    WatchHistoryResponse,
    HeartBeatData,
    CustomHeartBeatRes,
} from '../utils/types/Endeavor'
import {
    BillingAddressType,
    PersonalInfoType,
    UpdateBillingAddressType,
    UserAddress,
    UserPersonalInfo,
} from '../utils/types/Profile'
import {
    AddUserGiftPlan,
    BasketList,
    CurrentLicenceVoucherCode,
    GiftLicenceType,
    GiftUserPlan,
    GuestUserDetails,
    IPaymentDetailsType,
    PaymentArrangementTypes,
    PaymentInitTypes,
    PaymentSelectedEDVLicence,
    RetrieveGiftResponse,
    SubscriptionPlan,
    UserCardsTypes,
    UserPurchaseLicence,
    UserSubscribedLicence,
} from '../utils/types/common'

export const userEndeavorLogin = async (loginData: LoginFormTypes) => {
    try {
        const response = await Axios.edvApiCall<
            LoginFormTypes,
            UserLoginResponse
        >(AXIOS_METHODS.POST, EDVR_ENDPOINTS.USER_LOGIN, undefined, loginData)
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('login'))
    }
}

export const userEndeavorRegistration = async (
    registrationData: UserRegistrationTypes
) => {
    try {
        const response = await Axios.edvApiCall<
            UserRegistrationTypes,
            UserLoginResponse
        >(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.USER_REGISTRATION,
            undefined,
            registrationData
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('register', 'user'))
    }
}

export const guestCheckInEDV = async () => {
    try {
        const response = await Axios.edvApiCall<string, UserLoginResponse>(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.GUEST_CHECK_IN
        )
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const userEndeavorPreferences = async (
    preferencesData: UserEndeavorPreferences
) => {
    try {
        const response = await Axios.edvApiCall<
            UserEndeavorPreferences,
            string
        >(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.USER_PREFERENCES,
            undefined,
            preferencesData
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('update', 'user preference'))
    }
}

export const getUserEndeavorPreferences = async () => {
    try {
        const response = await Axios.edvApiCall<
            string,
            UserEndeavorPreferences
        >(AXIOS_METHODS.GET, EDVR_ENDPOINTS.USER_PREFERENCES)
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile(
                'fetching',
                'user preference'
            )
        )
    }
}

export const postUserAddress = async (addressData: UserAddress) => {
    try {
        const response = await Axios.edvApiCall<UserAddress, string>(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.USER_ADDRESS,
            undefined,
            addressData
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.errorOnAdd('address'))
    }
}

export const updateUserAddress = async (
    id: string,
    addressData: UpdateBillingAddressType
) => {
    try {
        const response = await Axios.edvApiCall<
            UpdateBillingAddressType,
            string
        >(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.UPDATE_USER_ADDRESS(id),
            undefined,
            addressData
        )
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        throw Error(MESSAGES.error.errorOnUpdate('Address'))
    }
}

export const getUserAddress = async () => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<BillingAddressType>
        >(AXIOS_METHODS.GET, EDVR_ENDPOINTS.GET_USER_ADDRESS)
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.getUserDataError('address'))
    }
}

export const endeavorLicenceList = async () => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<SubscriptionPlan>
        >(AXIOS_METHODS.GET, EDVR_ENDPOINTS.GET_LICENCE)
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.list('membership'))
    }
}

export const getGiftLicence = async () => {
    try {
        const response = await Axios.edvApiCall<string, Array<GiftLicenceType>>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_GIFT_LICENCE
        )
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.list('gift'))
    }
}

export const addUserGiftLicence = async (
    external_id: string,
    selectedPlan: GiftUserPlan
) => {
    try {
        const response = await Axios.edvApiCall<GiftUserPlan, AddUserGiftPlan>(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.POST_GIFT_LICENCE(external_id),
            undefined,
            selectedPlan
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('purchase', 'gift'))
    }
}

export const redeemGiftLicence = async (external_id: string) => {
    try {
        await Axios.edvApiCall<string, AddUserGiftPlan>(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.REDEEM_GIFT_LICENCE(external_id)
        )
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.messages?.[0]?.split?.(':')?.[0] ||
            MESSAGES.error.endpointError
        throw Error(errorMessage)
    }
}

export const retrieveGiftLicence = async (external_id: string) => {
    try {
        const response = await Axios.edvApiCall<
            GiftUserPlan,
            RetrieveGiftResponse
        >(AXIOS_METHODS.GET, EDVR_ENDPOINTS.RETRIEVE_GIFT_LICENCE(external_id))
        if (response) return { ...response, giftCode: external_id }
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.messages?.[0]?.split?.(':')?.[0] ||
            MESSAGES.error.endpointError
        throw Error(errorMessage)
    }
}

export const getPaymentDetails = async () => {
    try {
        const response = await Axios.edvApiCall<string, IPaymentDetailsType>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.PAYMENT_DETAILS
        )
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile(
                'fetching',
                'payment details'
            )
        )
    }
}

export const userSelectedBasketLicence = async () => {
    try {
        const response = await Axios.edvApiCall<string, BasketList>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.BASKET_CALL
        )
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const userSubscribedLicence = async () => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<UserSubscribedLicence>
        >(AXIOS_METHODS.GET, EDVR_ENDPOINTS.USER_SUBSCRIBED_LICENCE)
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('get', 'user membership'))
    }
}

export const videoPlaybackHeartBeat = async (data: Array<HeartBeatData>) => {
    try {
        const beaconUrl = process.env.REACT_APP_MWH_ENDEAVOR_BEACON_URL
            ? process.env.REACT_APP_MWH_ENDEAVOR_BEACON_URL
            : ''
        const response = await Axios.edvApiCall<
            string,
            Array<CustomHeartBeatRes>
        >(AXIOS_METHODS.POST, beaconUrl, undefined, data, true)
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('post', 'custom heartbeat'))
    }
}

export const cancelUserLicence = async (licenceId: number) => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<UserSubscribedLicence>
        >(AXIOS_METHODS.DELETE, EDVR_ENDPOINTS.CANCEL_LICENCE(licenceId))
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('cancel', 'user membership'))
    }
}

export const autoRenewalSubscriptionPlan = async (licenceId: number) => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<UserSubscribedLicence>
        >(AXIOS_METHODS.PATCH, EDVR_ENDPOINTS.AUTO_RENEW_LICENCE(licenceId))
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('auto renew', 'membership'))
    }
}

export const pauseUserLicence = async (licenceId: number) => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<UserSubscribedLicence>
        >(AXIOS_METHODS.POST, EDVR_ENDPOINTS.PAUSE_LICENCE(licenceId))
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('pause', 'membership'))
    }
}

export const resumeUserLicence = async (licenceId: number) => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<UserSubscribedLicence>
        >(AXIOS_METHODS.POST, EDVR_ENDPOINTS.RESUME_LICENCE(licenceId))
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('resume', 'membership'))
    }
}

export const applyVoucherCode = async (voucherCode: string) => {
    try {
        const response = await Axios.edvApiCall<
            string,
            Array<UserSubscribedLicence>
        >(AXIOS_METHODS.PUT, EDVR_ENDPOINTS.GIFT_VOUCHER(voucherCode))
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('applying', 'voucher')
        )
    }
}

export const applyVoucherCodeOnCurrentLicence = async (
    voucherCode: CurrentLicenceVoucherCode
) => {
    try {
        const response = await Axios.edvApiCall<
            CurrentLicenceVoucherCode,
            Array<UserSubscribedLicence>
        >(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.GIFT_VOUCHER_ON_CURRENT_LICENCE,
            undefined,
            voucherCode
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('applying', 'voucher')
        )
    }
}

export const setUserDefaultCard = async (cardId: number) => {
    try {
        const response = await Axios.edvApiCall<string, undefined>(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.DEFAULT_CARD(cardId)
        )
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('set', 'default card'))
    }
}

export const switchCardForRenewingLicence = async (
    licenceId: number,
    cardId: number
) => {
    try {
        const response = await Axios.edvApiCall<string, undefined>(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.SWITCH_CARD_FOR_ACTIVE_LICENCE(licenceId, cardId)
        )
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('set', 'default card'))
    }
}

export const addBasketToCart = async (basket_key: string) => {
    try {
        const response = await Axios.edvApiCall<string, BasketList>(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.PUT_BASKET(basket_key)
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const userPaymentInit = async (paymentInit: PaymentInitTypes) => {
    try {
        const response = await Axios.edvApiCall<
            PaymentInitTypes,
            PaymentInitTypes
        >(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.PAYMENT_INIT,
            undefined,
            paymentInit
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const deleteUserCard = async (cardId: number) => {
    try {
        const response = await Axios.edvApiCall<string, undefined>(
            AXIOS_METHODS.DELETE,
            EDVR_ENDPOINTS.DELETE_CARD(cardId)
        )
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('delete', 'card'))
    }
}

export const userBasketPayment = async (
    paymentInit: PaymentSelectedEDVLicence
) => {
    try {
        const response = await Axios.edvApiCall<
            PaymentSelectedEDVLicence,
            UserPurchaseLicence
        >(AXIOS_METHODS.POST, EDVR_ENDPOINTS.PAY_BASKET, undefined, paymentInit)
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const giftBasketPayment = async (
    paymentInit: PaymentArrangementTypes
) => {
    try {
        const response = await Axios.edvApiCall<
            PaymentArrangementTypes,
            undefined
        >(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.GIFT_PAY_BASKET,
            undefined,
            paymentInit
        )
        return response //Here we can't get any response body by EDV API, so directly return response for further API
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const getGiftBasket = async () => {
    try {
        const response = await Axios.edvApiCall<string, AddUserGiftPlan>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_GIFT_BASKET
        )
        if (response) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('get', 'gift'))
    }
}

export const removeGiftFromBasket = async (external_id: string) => {
    try {
        const response = await Axios.edvApiCall<string, AddUserGiftPlan>(
            AXIOS_METHODS.DELETE,
            EDVR_ENDPOINTS.REMOVE_ITEM_FROM_GIFT_BASKET(external_id)
        )
        if (response) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('remove', 'gift'))
    }
}

export const guestUserDetails = async (userDetails: GuestUserDetails) => {
    try {
        const response = await Axios.edvApiCall<GuestUserDetails, undefined>(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.GUEST_USER_DETAIL,
            undefined,
            userDetails
        )
        return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('fetching', 'user details')
        )
    }
}

export const userStoredCards = async () => {
    try {
        const response = await Axios.edvApiCall<string, UserCardsTypes>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.AVAILABLE_CARDS
        )
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('fetching', 'saved cards')
        )
    }
}

export const userEndeavorSetPassword = async (
    setPasswordData: SetPasswordFormTypes
) => {
    try {
        const response = await Axios.edvApiCall<
            SetPasswordFormTypes,
            undefined
        >(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.USER_SET_PASSWORD,
            undefined,
            setPasswordData
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('setting up', 'password')
        )
    }
}

export const userEndeavorResetPassword = async (
    resetPasswordData: ResetPasswordFormTypes
) => {
    try {
        const response = await Axios.edvApiCall<
            ResetPasswordFormTypes,
            undefined
        >(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.USER_RESET_PASSWORD_EMAIL,
            undefined,
            resetPasswordData
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('resetting', 'password')
        )
    }
}

export const userEndeavorUpdatePassword = async (
    resetPasswordData: UpdatePasswordTypes
) => {
    try {
        const response = await Axios.edvApiCall<UpdatePasswordTypes, undefined>(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.USER_UPDATE_PASSWORD,
            undefined,
            resetPasswordData
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('update', 'password')
        )
    }
}

export const userEndeavorLogout = async (refreshToken: LogoutTypes) => {
    try {
        const response = await Axios.edvApiCall<LogoutTypes, undefined>(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.USER_LOGOUT,
            undefined,
            refreshToken
        )
        return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('logout'))
    }
}

export const userDetails = async () => {
    try {
        const response = await Axios.edvApiCall<string, UserDetailsTypes>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.USER_PROFILE
        )
        if (response) return response
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('fetching', 'user detail')
        )
    }
}

export const updateUserDetails = async (
    userDetails: PersonalInfoType | UserPersonalInfo
) => {
    try {
        const response = await Axios.edvApiCall<
            PersonalInfoType | UserPersonalInfo,
            string
        >(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.USER_PROFILE,
            undefined,
            userDetails
        )
        if (response) return response
        else throw new Error(MESSAGES.error.endpointError)
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('updating', 'user detail')
        )
    }
}

export const getSection = async (
    name: string,
    items: number,
    lastSeen: number,
    bpp: number = 5
) => {
    try {
        const response = await Axios.edvApiCall<string, SectionResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_SECTION(name, items, lastSeen, bpp)
        )
        if (response?.buckets?.length) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const getBucketByExit = async (
    section: string,
    exid: string,
    lastSeen: number
) => {
    try {
        const response = await Axios.edvApiCall<string, BucketResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_BUCKET_BY_EXID(section, exid, lastSeen)
        )
        if (response && response?.contentList.length > 0) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const getPlaylist = async (id: number, lastSeen: number) => {
    try {
        const response = await Axios.edvApiCall<string, PlaylistResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_PLAYLIST(id, lastSeen)
        )
        if (response) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile('getting', 'playlist')
        )
    }
}

export const getVod = async (id: number) => {
    try {
        const response = await Axios.edvApiCall<string, VideoResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_VOD(id)
        )
        if (response) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('get', 'video'))
    }
}

export const getVodByTags = async (tags: string) => {
    try {
        const response = await Axios.edvApiCall<string, VideosByTagsResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_VOD_BY_TAGS(tags)
        )
        if (response && response?.contentItems.length > 0) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedToGetVideos)
    }
}

export const getWatchHistory = async (lastSeen: number, rpp: number) => {
    try {
        const response = await Axios.edvApiCall<string, WatchHistoryResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.WATCH_HISTORY(lastSeen, rpp)
        )
        if (response && response?.vods.length > 0) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('get', 'watch history'))
    }
}

export const getFavorites = async (p: number) => {
    try {
        const response = await Axios.edvApiCall<string, FavouritesResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_FAVOURITES(p)
        )
        if (response && response?.events.length > 0) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('get', 'favourite videos'))
    }
}

export const addToFavourite = async (id: number) => {
    try {
        await Axios.edvApiCall<string, null>(
            AXIOS_METHODS.PUT,
            EDVR_ENDPOINTS.ADD_DELETE_FAVOURITE(id)
        )
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile(
                'adding',
                'video to favourite'
            )
        )
    }
}

export const deleteFromFavorite = async (id: number) => {
    try {
        await Axios.edvApiCall<string, null>(
            AXIOS_METHODS.DELETE,
            EDVR_ENDPOINTS.ADD_DELETE_FAVOURITE(id)
        )
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(
            MESSAGES.error.somethingWentWrongWhile(
                'removing',
                'video from favourite'
            )
        )
    }
}

export const redeemPin = async (pinData: RedeemPin) => {
    try {
        await Axios.edvApiCall<RedeemPin, undefined>(
            AXIOS_METHODS.POST,
            EDVR_ENDPOINTS.REDEEM_PIN,
            undefined,
            pinData
        )
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const getUpcomingEvents = async () => {
    try {
        const response = await Axios.edvApiCall<string, EventsResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.UPCOMING_EVENTS
        )
        if (response) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const getLiveEvents = async () => {
    try {
        const response = await Axios.edvApiCall<string, EventsResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.LIVE_EVENTS
        )
        if (response) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.endpointError)
    }
}

export const getEvent = async (id: number) => {
    try {
        const response = await Axios.edvApiCall<string, VideoResponse>(
            AXIOS_METHODS.GET,
            EDVR_ENDPOINTS.GET_EVENT(id)
        )
        if (response) {
            return response
        }
    } catch (error) {
        console.error(error) // as per sonar cloud
        throw Error(MESSAGES.error.failedTo('get', 'video'))
    }
}
